<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useAlert } from 'dashboard/composables';
import { useCaptain } from 'dashboard/composables/useCaptain';
import { useCaptainConfigStore } from 'dashboard/store/captain/preferences';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SectionLayout from '../account/components/SectionLayout.vue';
import ModelSelector from './components/ModelSelector.vue';
import FeatureToggle from './components/FeatureToggle.vue';
import CaptainPaywall from 'next/captain/pageComponents/Paywall.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const { captainEnabled } = useCaptain();

const captainConfigStore = useCaptainConfigStore();
const { uiFlags, credentials } = storeToRefs(captainConfigStore);

const isLoading = computed(() => uiFlags.value.isFetching);

const apiKeyInput = ref('');
const endpointInput = ref('');
const isSavingConnection = ref(false);

watch(
  credentials,
  value => {
    endpointInput.value = value?.endpoint || '';
  },
  { immediate: true }
);

const apiKeyPlaceholder = computed(() =>
  credentials.value?.api_key_set
    ? t('CAPTAIN_SETTINGS.CONNECTION.API_KEY_SET_PLACEHOLDER')
    : ''
);

async function handleConnectionSave() {
  const payload = {};
  if (apiKeyInput.value.trim()) payload.api_key = apiKeyInput.value.trim();
  if (endpointInput.value.trim()) payload.endpoint = endpointInput.value.trim();

  isSavingConnection.value = true;
  try {
    await captainConfigStore.updatePreferences({
      captain_credentials: payload,
    });
    apiKeyInput.value = '';
    useAlert(t('CAPTAIN_SETTINGS.API.SUCCESS'));
  } catch (error) {
    useAlert(t('CAPTAIN_SETTINGS.API.ERROR'));
  } finally {
    isSavingConnection.value = false;
  }
}

const modelFeatures = computed(() => [
  {
    key: 'editor',
    title: t('CAPTAIN_SETTINGS.MODEL_CONFIG.EDITOR.TITLE'),
    description: t('CAPTAIN_SETTINGS.MODEL_CONFIG.EDITOR.DESCRIPTION'),
  },
  {
    key: 'copilot',
    title: t('CAPTAIN_SETTINGS.MODEL_CONFIG.COPILOT.TITLE'),
    description: t('CAPTAIN_SETTINGS.MODEL_CONFIG.COPILOT.DESCRIPTION'),
  },
  {
    key: 'prefill',
    title: t('CAPTAIN_SETTINGS.MODEL_CONFIG.PREFILL.TITLE'),
    description: t('CAPTAIN_SETTINGS.MODEL_CONFIG.PREFILL.DESCRIPTION'),
  },
  {
    key: 'detection',
    title: t('CAPTAIN_SETTINGS.MODEL_CONFIG.DETECTION.TITLE'),
    description: t('CAPTAIN_SETTINGS.MODEL_CONFIG.DETECTION.DESCRIPTION'),
  },
]);

const featureToggles = computed(() => [
  {
    key: 'assistant',
  },
  {
    key: 'label_suggestion',
  },
  {
    key: 'help_center_search',
  },
  {
    key: 'audio_transcription',
  },
  {
    key: 'translation',
  },
]);

const shouldShowFeature = () => true;

const isFeatureAccessible = () => true;

async function handleFeatureToggle({ feature, enabled }) {
  try {
    await captainConfigStore.updatePreferences({
      captain_features: { [feature]: enabled },
    });
    useAlert(t('CAPTAIN_SETTINGS.API.SUCCESS'));
  } catch (error) {
    useAlert(t('CAPTAIN_SETTINGS.API.ERROR'));
    captainConfigStore.fetch();
  }
}

async function handleModelChange({ feature, model }) {
  try {
    await captainConfigStore.updatePreferences({
      captain_models: { [feature]: model },
    });
    useAlert(t('CAPTAIN_SETTINGS.API.SUCCESS'));
  } catch (error) {
    useAlert(t('CAPTAIN_SETTINGS.API.ERROR'));
    captainConfigStore.fetch();
  }
}

onMounted(() => {
  captainConfigStore.fetch();
});
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :no-records-message="t('CAPTAIN_SETTINGS.NOT_ENABLED')"
    :loading-message="t('CAPTAIN_SETTINGS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('CAPTAIN_SETTINGS.TITLE')"
        :description="t('CAPTAIN_SETTINGS.DESCRIPTION')"
        :link-text="t('CAPTAIN_SETTINGS.LINK_TEXT')"
        icon-name="captain"
        feature-name="captain_billing"
      />
    </template>
    <template #body>
      <div v-if="captainEnabled" class="flex flex-col gap-1">
        <!-- Connection Section -->
        <SectionLayout
          :title="t('CAPTAIN_SETTINGS.CONNECTION.TITLE')"
          :description="t('CAPTAIN_SETTINGS.CONNECTION.DESCRIPTION')"
        >
          <div class="grid gap-4 max-w-lg">
            <NextInput
              v-model="apiKeyInput"
              type="password"
              autocomplete="off"
              :label="t('CAPTAIN_SETTINGS.CONNECTION.API_KEY_LABEL')"
              :placeholder="apiKeyPlaceholder"
            />
            <NextInput
              v-model="endpointInput"
              type="text"
              :label="t('CAPTAIN_SETTINGS.CONNECTION.ENDPOINT_LABEL')"
              placeholder="https://openrouter.ai/api/v1"
            />
            <div>
              <NextButton
                :label="t('CAPTAIN_SETTINGS.CONNECTION.SAVE')"
                :is-loading="isSavingConnection"
                @click="handleConnectionSave"
              />
            </div>
            <p class="text-sm text-n-slate-11">
              {{ t('CAPTAIN_SETTINGS.CONNECTION.RESTART_HINT') }}
            </p>
          </div>
        </SectionLayout>

        <!-- Model Configuration Section -->
        <SectionLayout
          with-border
          :title="t('CAPTAIN_SETTINGS.MODEL_CONFIG.TITLE')"
          :description="t('CAPTAIN_SETTINGS.MODEL_CONFIG.DESCRIPTION')"
        >
          <div class="grid gap-4">
            <ModelSelector
              v-for="feature in modelFeatures"
              v-show="shouldShowFeature(feature)"
              :key="feature.key"
              :is-allowed="isFeatureAccessible(feature)"
              :feature-key="feature.key"
              :title="feature.title"
              :description="feature.description"
              @change="handleModelChange"
            />
          </div>
        </SectionLayout>

        <!-- Features Section -->
        <SectionLayout
          :title="t('CAPTAIN_SETTINGS.FEATURES.TITLE')"
          :description="t('CAPTAIN_SETTINGS.FEATURES.DESCRIPTION')"
          with-border
        >
          <div class="grid gap-4">
            <FeatureToggle
              v-for="feature in featureToggles"
              v-show="shouldShowFeature(feature)"
              :key="feature.key"
              :is-allowed="isFeatureAccessible(feature)"
              :feature-key="feature.key"
              @change="handleFeatureToggle"
              @model-change="handleModelChange"
            />
          </div>
        </SectionLayout>
      </div>
      <div v-else>
        <CaptainPaywall />
      </div>
    </template>
  </SettingsLayout>
</template>
